import { jsonHeaders, mfaHeaders } from '~/api/auth-headers';
import { backendApiGateway } from '~/client/backend';
import { TotpSetup, TotpChallengeResult, TotpVerifyResult } from '~/types/totp';

/**
 * Starts TOTP enrollment for the current user: the backend generates and
 * encrypts a fresh secret and returns the Base32 secret plus the `otpauth://`
 * URI to render as a QR code. Calling this again overwrites the previous secret.
 *
 * Reconfiguring an already-enrolled user is a sensitive action (a hijacked
 * session could swap the second factor), so the backend requires step-up:
 * without a valid `password` it rejects with 403 ("step-up required"); an
 * incorrect password rejects with 422. Initial enrollment needs no password.
 */
export const setupTotp = async (password?: string): Promise<TotpSetup> => {
  const { data } = await backendApiGateway.post<TotpSetup>(
    'api/auth/totp/setup',
    password ? { password } : undefined,
    jsonHeaders,
  );
  return data;
};

/**
 * Disables the authenticator app: clears the user's TOTP secret and enrollment.
 * Sensitive action requiring step-up — without a valid `password` the backend
 * rejects with 403 ("sudo required") when outside the reauth window; an
 * incorrect password rejects with 422; 404 means there was nothing to remove.
 */
export const deleteTotp = async (password?: string): Promise<void> => {
  await backendApiGateway.delete('api/auth/totp', {
    data: password ? { password } : undefined,
    ...jsonHeaders,
  });
};

/**
 * Validates a 6-digit TOTP code against the secret saved during setup.
 * Rejects with 422 when the code is invalid or expired, 401 when the user
 * never completed setup.
 */
export const verifyTotp = async (code: string): Promise<TotpVerifyResult> => {
  const { data } = await backendApiGateway.post<TotpVerifyResult>('api/auth/totp/verify', { code }, jsonHeaders);
  return data;
};

/**
 * Completes the login (phase 2) by validating a TOTP code against the pre-auth
 * `mfa_token`. On success the backend issues the session (cookie) and returns
 * `{ token }`. Rejects with 401 (mfa_token missing/expired → back to login) or
 * 422 (wrong/expired code → re-enter the current code).
 */
export const challengeTotp = async (code: string, mfaToken: string): Promise<TotpChallengeResult> => {
  const { data } = await backendApiGateway.post<TotpChallengeResult>(
    'api/auth/totp/challenge',
    { code },
    mfaHeaders(mfaToken),
  );
  return data;
};
